import type { AiToolkitAssistantFollowUp } from "@mem-client/ai-toolkit/contract";
import type { AssistantFollowUpObservable } from "@/store/assistant/AssistantFollowUpObservable";
import {
  AssistantChangeRequestChangeKind,
  AssistantFollowUpStatus,
  type AssistantFollowUpChangeRequestModelData,
  type AssistantFollowUpModelData,
} from "@/store/assistant/types";
import type { SyncUpdateValue } from "@/store/sync/types";

type AssistantFollowUpSource = Pick<
  AssistantFollowUpObservable,
  | "id"
  | "assistantId"
  | "projectId"
  | "taskId"
  | "sourceContextId"
  | "content"
  | "status"
  | "scheduledFor"
  | "firedAt"
  | "completedAt"
  | "commitmentKind"
  | "dueAt"
  | "deadlineAt"
  | "triggerType"
  | "triggerNotBefore"
  | "triggerNotAfter"
  | "createdAt"
  | "updatedAt"
>;

export const buildAssistantFollowUp = ({
  followUp,
}: {
  followUp: AssistantFollowUpSource;
}): AiToolkitAssistantFollowUp => ({
  id: followUp.id,
  assistantId: followUp.assistantId,
  projectId: followUp.projectId,
  taskId: followUp.taskId,
  sourceContextId: followUp.sourceContextId,
  content: followUp.content,
  status: followUp.status,
  scheduledFor: followUp.scheduledFor,
  firedAt: followUp.firedAt,
  completedAt: followUp.completedAt,
  commitmentKind: followUp.commitmentKind,
  dueAt: followUp.dueAt,
  deadlineAt: followUp.deadlineAt,
  triggerType: followUp.triggerType,
  triggerNotBefore: followUp.triggerNotBefore,
  triggerNotAfter: followUp.triggerNotAfter,
  createdAt: followUp.createdAt,
  updatedAt: followUp.updatedAt,
});

export const buildAssistantFollowUpFromSyncUpdateValue = ({
  updateValue,
}: {
  updateValue: SyncUpdateValue<AssistantFollowUpModelData>;
}): AiToolkitAssistantFollowUp => {
  const followUp = updateValue.model_data;

  return {
    id: updateValue.model_id,
    assistantId: followUp.assistant_id,
    projectId: followUp.project_id,
    taskId: followUp.task_id,
    sourceContextId: followUp.source_context_id,
    content: followUp.content,
    status: followUp.status,
    scheduledFor: followUp.scheduled_for,
    firedAt: followUp.fired_at,
    completedAt: followUp.completed_at,
    commitmentKind: followUp.commitment_kind,
    dueAt: followUp.due_at,
    deadlineAt: followUp.deadline_at,
    triggerType: followUp.trigger_type,
    triggerNotBefore: followUp.trigger_not_before,
    triggerNotAfter: followUp.trigger_not_after,
    createdAt: followUp.created_at,
    updatedAt: followUp.updated_at,
  };
};

export const isCreateFollowUpChangeRequestUpdateValue = ({
  updateValue,
}: {
  updateValue: SyncUpdateValue<AssistantFollowUpChangeRequestModelData>;
}): boolean => updateValue.model_data.change_kind === AssistantChangeRequestChangeKind.Create;

export const buildAssistantFollowUpFromCreateChangeRequestUpdateValue = ({
  updateValue,
}: {
  updateValue: SyncUpdateValue<AssistantFollowUpChangeRequestModelData>;
}): AiToolkitAssistantFollowUp => {
  const changeRequest = updateValue.model_data;

  return {
    id: updateValue.model_id,
    assistantId: changeRequest.assistant_id,
    projectId: changeRequest.requested_project_id,
    taskId: changeRequest.requested_task_id,
    sourceContextId: null,
    content: changeRequest.requested_content ?? "",
    status: changeRequest.requested_status ?? AssistantFollowUpStatus.Pending,
    scheduledFor: changeRequest.requested_scheduled_for ?? changeRequest.requested_at,
    firedAt: null,
    completedAt: null,
    commitmentKind: changeRequest.requested_commitment_kind,
    dueAt: changeRequest.requested_due_at,
    deadlineAt: changeRequest.requested_deadline_at,
    triggerType: changeRequest.requested_trigger_type,
    triggerNotBefore: changeRequest.requested_trigger_not_before,
    triggerNotAfter: changeRequest.requested_trigger_not_after,
    createdAt: changeRequest.created_at,
    updatedAt: changeRequest.updated_at,
    pending: true,
  };
};
